import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { CustomSelector } from "../../../components/CustomSelector";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomTextField } from "../../../components/CustomTextField";
import { useAuth } from "../../../components/contexts/AuthContext";
import {
  CONTACT_STATUS_OPTIONS,
  clientifyErrorMessage,
  createClientifyContact,
  deleteClientifyContact,
  getClientifyContact,
  updateClientifyContact,
} from "../../../services/clientifyService";
import { ClientifySidePanel, PanelField } from "./ClientifySidePanel";
import { ClientifyDeleteModal, DeleteIconButton } from "./ClientifyDeleteModal";
import { TagsInput } from "./ClientifyFormFields";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  title: "",
  status: "cold-lead",
  contact_source: "",
  tags: [],
  remarks: "",
});

const formFromContact = (contact) => ({
  first_name: contact.first_name || "",
  last_name: contact.last_name || "",
  email: contact.emails?.[0]?.email || "",
  phone: contact.phones?.[0]?.phone || "",
  company: contact.company_name || "",
  title: contact.title || "",
  status: contact.status || "",
  contact_source: contact.contact_source || "",
  tags: Array.isArray(contact.tags) ? contact.tags : [],
  remarks: contact.remarks || "",
});

const toPayload = (form) => {
  const payload = {
    ...form,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
  };
  if (!payload.status) delete payload.status;
  return payload;
};

const changedFields = (next, initial) =>
  Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) => JSON.stringify(value ?? null) !== JSON.stringify(initial[key] ?? null)
    )
  );

/**
 * "Agregar Contacto" / "Editar contacto" side panel, same field order as Clientify.
 *
 * There is no owner field: a record is created for the caller, or for a
 * superadmin, for the owner picked in the page filter (`ownerUserId`).
 */
export const ContactFormPanel = ({ open, contactId, ownerUserId, onClose, onSaved }) => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const isEdit = Boolean(contactId);
  const needsOwner = !isEdit && isSuperadmin && !ownerUserId;
  const [form, setForm] = useState(() => emptyForm());
  const [initialPayload, setInitialPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data: contact, isLoading: contactLoading } = useQuery({
    queryKey: ["clientify-contact", contactId],
    queryFn: () => getClientifyContact(contactId),
    enabled: open && isEdit,
    staleTime: 0,
    gcTime: 0,
  });

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    if (isEdit) {
      if (!contact) return;
      const next = formFromContact(contact);
      setForm(next);
      setInitialPayload(toPayload(next));
    } else {
      setForm(emptyForm());
      setInitialPayload(null);
    }
    initializedRef.current = true;
  }, [open, isEdit, contact]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const emailInvalid = Boolean(form.email.trim()) && !EMAIL_PATTERN.test(form.email.trim());
  const canSubmit =
    form.first_name.trim().length > 0 &&
    !emailInvalid &&
    !needsOwner;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const payload = changedFields(toPayload(form), initialPayload || {});
        if (!Object.keys(payload).length) {
          onClose();
          return;
        }
        saved = await updateClientifyContact(contactId, payload);
        toast.success("Contacto actualizado");
      } else {
        saved = await createClientifyContact({
          ...toPayload(form),
          owner_user: isSuperadmin ? ownerUserId : undefined,
        });
        toast.success("Contacto creado");
      }
      onSaved(saved);
      onClose();
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo guardar el contacto"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClientifyContact(contactId);
      toast.success("Contacto eliminado");
      setConfirmDelete(false);
      onSaved();
      onClose();
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo eliminar el contacto"));
    } finally {
      setDeleting(false);
    }
  };

  const contactName =
    contact && ([contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.full_name);

  return (
    <>
    <ClientifySidePanel
      open={open}
      title={isEdit ? "Editar contacto" : "Agregar Contacto"}
      onClose={onClose}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      saving={saving}
      loading={isEdit && contactLoading}
      headerActions={
        isEdit && contact ? (
          <DeleteIconButton
            label="Eliminar contacto"
            onClick={() => setConfirmDelete(true)}
            disabled={saving || deleting}
          />
        ) : null
      }
    >
      {needsOwner && (
        <Typography sx={{ fontSize: "0.8125rem", color: "warning.main" }}>
          Selecciona un propietario en el filtro de la página para crear el contacto.
        </Typography>
      )}

      <PanelField label="Nombre" required>
        <CustomTextField
          value={form.first_name}
          onChange={(e) => set("first_name")(e.target.value)}
        />
      </PanelField>

      <PanelField label="Apellido">
        <CustomTextField
          value={form.last_name}
          onChange={(e) => set("last_name")(e.target.value)}
        />
      </PanelField>

      <PanelField label="Correo" error={emailInvalid ? "Introduce un correo válido" : undefined}>
        <CustomTextField
          type="email"
          value={form.email}
          onChange={(e) => set("email")(e.target.value)}
        />
      </PanelField>

      <PanelField label="Teléfono">
        <CustomTextField
          type="tel"
          placeholder="+34"
          value={form.phone}
          onChange={(e) => set("phone")(e.target.value)}
        />
      </PanelField>

      <PanelField label="Empresa">
        <CustomTextField value={form.company} onChange={(e) => set("company")(e.target.value)} />
      </PanelField>

      <PanelField label="Cargo">
        <CustomTextField value={form.title} onChange={(e) => set("title")(e.target.value)} />
      </PanelField>

      <PanelField label="Estado">
        <CustomSelector
          placeholder="Selecciona un estado"
          value={form.status}
          onChange={set("status")}
          options={CONTACT_STATUS_OPTIONS}
        />
      </PanelField>

      <PanelField label="Origen del contacto">
        <CustomTextField
          value={form.contact_source}
          onChange={(e) => set("contact_source")(e.target.value)}
        />
      </PanelField>

      <PanelField label="Etiquetas">
        <TagsInput value={form.tags} onChange={set("tags")} />
      </PanelField>

      <PanelField label="Observaciones">
        <CustomTextArea
          rows={4}
          value={form.remarks}
          onChange={(e) => set("remarks")(e.target.value)}
        />
      </PanelField>
    </ClientifySidePanel>

    <ClientifyDeleteModal
      open={confirmDelete}
      noun="contacto"
      name={contactName}
      deleting={deleting}
      onCancel={() => setConfirmDelete(false)}
      onConfirm={handleDelete}
    />
    </>
  );
};

ContactFormPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  contactId: PropTypes.number,
  ownerUserId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
